import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import debounce from 'lodash/debounce';
import SearchPreviewResultCompany from './SearchPreviewResultCompany';
import SearchService from '../services/SearchService';
import { trackRecentlyViewedCompany } from '../utils/searchUtils';
import '../styles/SearchPreview.css';
import { useAuth } from './AuthContext';

const ANIMATION_DURATION = 200;
const DEBOUNCE_MS = 200; // 200ms debounce for typing

const SearchPreviewNew = ({
  query,
  parentRef,
  anchorRef,
  onlyShowCompanySearch = false,
  handleSearch,
  handleClick,
  previewTextOverride,
  setCompany,
  onClear,
  onVisibilityChange,
  isVisible: externalIsVisible,
  isClickingPreviewItemRef
}) => {
  const [results, setResults] = useState([]);
  const [position, setPosition] = useState({ x: 0, y: 0, width: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCompanyResults, setShowCompanyResults] = useState(false);
  const previewRef = useRef(null);
  const navigate = useNavigate();
  const animationTimeoutRef = useRef(null);
  const lastQueryRef = useRef('');
  const { serverAddress, flaskServerAddress } = useAuth();
  const searchService = useMemo(
    () => new SearchService(flaskServerAddress, serverAddress),
    [flaskServerAddress, serverAddress]
  );

  // Position the preview dropdown
  const updatePosition = useCallback(() => {
    const referenceElement = anchorRef?.current || parentRef.current;
    if (!referenceElement || !isActive) return;

    const rect = referenceElement.getBoundingClientRect();
    setPosition({
      x: rect.left,
      y: rect.bottom,
      width: rect.width
    });
  }, [parentRef, anchorRef, isActive]);

  // Override local visibility state with external control if provided
  useEffect(() => {
    if (externalIsVisible !== undefined) {
      setIsVisible(externalIsVisible);
      if (externalIsVisible) {
        setIsActive(true); 
        updatePosition(); // Recalculate position when becoming visible
      } else {
        animationTimeoutRef.current = setTimeout(() => {
          setIsActive(false);
        }, ANIMATION_DURATION);
      }
    }
  }, [externalIsVisible, updatePosition]);

  // Update debounced search function to handle transitions better
  const debouncedSearch = useRef(
    debounce(async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) {
        setResults([]);
        setIsLoading(false);
        setShowCompanyResults(false);
        return;
      }

      // Set loading state but don't hide existing results immediately
      // This prevents jarring layout changes
      setIsLoading(true);
      setIsVisible(true);

      try {
        const searchResults = await searchService.searchCompanies(searchTerm);
        
        // Only update results if this is still the latest query
        if (searchTerm === lastQueryRef.current) {
          // First prepare the new results without showing them
          setResults(searchResults);
          
          // Then transition states with a slight delay
          setTimeout(() => {
            setIsLoading(false);
            if (searchResults && searchResults.length > 0) {
              setShowCompanyResults(true);
            } else {
              setShowCompanyResults(false);
            }
          }, 50);
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
        setTimeout(() => {
          setIsLoading(false);
          setShowCompanyResults(false);
        }, 50);
      }
    }, DEBOUNCE_MS)
  ).current;

  // Cleanup function for animations and debounce
  const cleanup = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Update search when query changes
  useEffect(() => {
    console.log('[SearchPreviewNew] Query changed:', query);
    lastQueryRef.current = query;
    
    // Immediately hide preview for empty queries
    if (!query || query.trim().length === 0) {
      console.log('[SearchPreviewNew] Empty query, hiding preview');
      setIsVisible(false);
      setShowCompanyResults(false);
      setResults([]);
      return;
    }
    
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  // Effect to update position when anchorRef or isActive changes
  useEffect(() => {
    const referenceElement = anchorRef?.current || parentRef.current;
    if (!referenceElement || !isActive) return;

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(referenceElement);
    updatePosition(); // Initial position calculation

    return () => resizeObserver.disconnect();
  }, [updatePosition, isActive, anchorRef, parentRef]);

  // Handle visibility changes
  useEffect(() => {
    console.log('[SearchPreviewNew] Visibility changed:', { isVisible, isActive, showCompanyResults });
    onVisibilityChange?.(isVisible);
  }, [isVisible, isActive, showCompanyResults, onVisibilityChange]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(onlyShowCompanySearch ? 0 : 0);
  }, [results, onlyShowCompanySearch]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key) || !results.length) return;

    e.preventDefault();
    e.stopPropagation();

    switch (e.key) {
      case 'ArrowDown':
        setSelectedIndex(prev => 
          prev < (onlyShowCompanySearch ? results.length - 1 : results.length) 
            ? prev + 1 
            : 0
        );
        break;
      case 'ArrowUp':
        setSelectedIndex(prev => 
          prev > 0 
            ? prev - 1 
            : (onlyShowCompanySearch ? results.length - 1 : results.length)
        );
        break;
      case 'Enter':
        if (selectedIndex === 0 && !onlyShowCompanySearch) {
          handleSearch?.(query);
        } else {
          const company = results[onlyShowCompanySearch ? selectedIndex : selectedIndex - 1];
          if (company) {
            handleItemClick(company);
          }
        }
        break;
    }
  }, [results, selectedIndex, onlyShowCompanySearch, handleSearch, query]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  const handleItemClick = useCallback((company) => {
    // Track this company for faster future access
    trackRecentlyViewedCompany(company);
    
    // Prioritize the handleClick prop from the parent (HeaderSearchBar)
    if (handleClick) {
      handleClick(company); // Let HeaderSearchBar handle navigation and state reset
    } else if (setCompany) {
      // Handle the setCompany case if provided
      setCompany(company);
      // Manually hide if using setCompany directly without navigation causing blur
      setIsVisible(false);
      setShowCompanyResults(false);
      setIsActive(false);
      onClear?.();
    } else {
      // Fallback/default navigation logic if neither handleClick nor setCompany is provided
      navigate(`/company/${company.id}`, {
        state: { source: 'search' }
      });
      // Manually hide/clear for default navigation
      setIsVisible(false);
      setShowCompanyResults(false);
      setIsActive(false);
      onClear?.();
    }
    
    // Remove any pending animation (still useful)
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
  }, [handleClick, setCompany, navigate, onClear]);

  // Update focus and keydown handlers (keep Escape handling here)
  const handleFocus = useCallback(() => {
    if (query && query.length >= 2) {
      debouncedSearch.cancel(); 
      setIsLoading(true);
      searchService.searchCompanies(query)
        .then(results => {
          setResults(results || []);
          if (results && results.length > 0) {
            setShowCompanyResults(true);
          }
          setIsLoading(false);
        })
        .catch(error => {
          console.error('[SearchPreviewNew] Search error on focus:', error);
          setIsLoading(false);
        });
    }
  }, [query, debouncedSearch, searchService]);

  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape' && (isVisible || isActive)) {
      // Cancel any pending animations or searches
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      debouncedSearch.cancel();
      
      // Signal parent to close (e.g., by calling onClear or a new prop)
      // For now, just blur the input, parent's blur handler should take care of it
      if (parentRef.current) {
        parentRef.current.blur(); 
      }
      
      e.preventDefault();
      e.stopPropagation();
    }
  }, [isVisible, isActive, debouncedSearch, parentRef]);

  useEffect(() => {
    const input = parentRef.current;
    if (input) {
      input.addEventListener('keydown', handleEscapeKey);
    }
    
    return () => {
      if (input) {
        input.removeEventListener('keydown', handleEscapeKey);
      }
      cleanup();
    };
  }, [parentRef, handleEscapeKey, cleanup]);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(false);
      setShowCompanyResults(false);
      animationTimeoutRef.current = setTimeout(() => {
        setIsActive(false);
      }, ANIMATION_DURATION);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add a hover effect to keep results visible while hovering
  useEffect(() => {
    // When the preview gets rendered, add mouseover/mouseout listeners to prevent closing
    if (previewRef.current) {
      const handlePreviewMouseOver = () => {
        // Keep visible while hovering
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
          animationTimeoutRef.current = null;
        }
      };

      const handlePreviewMouseOut = () => {
        // When mouse leaves the preview, only close if the input isn't focused
        if (!parentRef.current?.matches(':focus')) {
          setIsVisible(false);
          setShowCompanyResults(false);
          animationTimeoutRef.current = setTimeout(() => {
            setIsActive(false);
          }, ANIMATION_DURATION);
        }
      };

      const previewElement = previewRef.current;
      previewElement.addEventListener('mouseover', handlePreviewMouseOver);
      previewElement.addEventListener('mouseout', handlePreviewMouseOut);

      return () => {
        previewElement.removeEventListener('mouseover', handlePreviewMouseOver);
        previewElement.removeEventListener('mouseout', handlePreviewMouseOut);
      };
    }
  }, [isVisible, parentRef]);

  // Add cleanup effect to ensure everything is reset when component unmounts
  useEffect(() => {
    // Reset everything when component unmounts
    return () => {
      // Cancel any pending debounced searches
      debouncedSearch.cancel();
      
      // Cancel any pending animations
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
      
      // Ensure search input isn't left focused
      if (parentRef.current) {
        parentRef.current.blur();
        
        // Reset any parent container styles
        const searchContainer = parentRef.current?.closest('.search-box-exterior');
        if (searchContainer) {
          searchContainer.style.width = '';
        }
      }
      
      // Remove any preview elements from the DOM that might be lingering
      const previewElements = document.querySelectorAll('.search-preview-container');
      previewElements.forEach(el => {
        if (el.parentNode) {
          el.remove();
        }
      });
    };
  }, [debouncedSearch, parentRef]);

  // Render preview with consistent spacing
  const renderPreview = () => {
    // If external visibility control exists, respect it first
    if (externalIsVisible === false) return null;
    // Otherwise use internal state
    if (!isActive || !query) return null;

    const previewContent = (
      <div
        ref={previewRef}
        className={`search-preview-container ${(externalIsVisible !== undefined ? externalIsVisible : isVisible) ? 'visible' : ''}`}
        style={{
          position: 'fixed',
          top: position.y,
          left: position.x,
          width: position.width,
        }}
      >
        {/* Search option is always visible */}
        {!onlyShowCompanySearch && (
          <div
            className={`search-preview-item ${selectedIndex === 0 ? 'selected' : ''}`}
            onClick={() => handleSearch?.(query)}
          >
            {previewTextOverride || `Search for Scuttlebutt about "${query}"`}
          </div>
        )}
        
        {/* Container for results with consistent spacing */}
        <div className="search-preview-results-wrapper">
          {/* Loading indicator */}
          
          {/* Results when available */}
          {!isLoading && showCompanyResults && results.length > 0 && (
            <div className="search-preview-company-results">
              {results.map((company, index) => {
                const itemIndex = onlyShowCompanySearch ? index : index + 1;
                return (
                  <SearchPreviewResultCompany 
                    key={company.id}
                    company={company}
                    isSelected={itemIndex === selectedIndex}
                    onClick={handleItemClick}
                    isClickingPreviewItemRef={isClickingPreviewItemRef}
                  />
                );
              })}
            </div>
          )}
          
          {/* Empty spacer to maintain height when no results */}
          {!isLoading && (!showCompanyResults || results.length === 0) && (
            <div className="search-preview-empty-spacer"></div>
          )}
        </div>
      </div>
    );

    return createPortal(previewContent, document.body);
  };

  return renderPreview();
};

export default SearchPreviewNew; 